// ─── Spot ────────────────────────────────────────────────────────────────────

export type SpotStatus = 'free' | 'occupied' | 'unavailable';

// ─── Parking attributes (migration 061) ──────────────────────────────────────
//
// Closed-enum metadata describing each spot's physical characteristics,
// access constraints, and amenities. Every column defaults to 'unknown'
// so existing rows remain valid until residents/admins fill them in.

export type ParkingStructureType =
  | 'regular'
  | 'hydraulic_lift_top'
  | 'hydraulic_lift_bottom'
  | 'robotic'
  | 'unknown';

export type VehicleSizeCompatibility =
  | 'all_suv'        // fits everything up to SUV
  | 'sedan_smaller'  // sedan and smaller only
  | 'mini_only'      // mini/small car only
  | 'unknown';

export type DependencyStatus =
  | 'independent'
  | 'tandem_general'        // tandem-coupled, front/back unspecified
  | 'tandem_front_blocking' // front spot — blocks the one behind
  | 'tandem_back_blocked'   // back spot — blocked by the one in front
  | 'unknown';

export interface ParkingSpotAttributes {
  structure_type:    ParkingStructureType;
  vehicle_size:      VehicleSizeCompatibility;
  dependency_status: DependencyStatus;
}

export const PARKING_STRUCTURE_TYPES: ParkingStructureType[] = [
  'regular', 'hydraulic_lift_top', 'hydraulic_lift_bottom', 'robotic', 'unknown',
];
export const VEHICLE_SIZES: VehicleSizeCompatibility[] = [
  'all_suv', 'sedan_smaller', 'mini_only', 'unknown',
];
export const DEPENDENCY_STATUSES: DependencyStatus[] = [
  'independent', 'tandem_general', 'tandem_front_blocking', 'tandem_back_blocked', 'unknown',
];

/** Driver vehicle size (selected on the user profile). */
export type DriverVehicleSize = 'mini' | 'sedan' | 'suv';

/**
 * Hierarchy check: can a driver of `driver` size park in a spot tagged
 * `spot`? Returns `'maybe'` when the spot's value is 'unknown' so callers
 * can render a warning instead of hiding the row.
 */
export function vehicleSizeFits(
  driver: DriverVehicleSize,
  spot: VehicleSizeCompatibility,
): true | false | 'maybe' {
  if (spot === 'unknown') return 'maybe';
  if (driver === 'mini') return true;
  if (driver === 'sedan') return spot === 'all_suv' || spot === 'sedan_smaller';
  return spot === 'all_suv';
}

export type AvailabilitySource = 'schedule' | 'window';

export interface ParkingSpot {
  id: string;
  spot_number: string;
  level_label: string;
  status: SpotStatus;
  owner_id: string;
  building_id: string;
}

export interface FreeSpot extends ParkingSpot {
  available_until: string | null;
  availability_source: AvailabilitySource | null;
  owner: { apartment: string };
}

// ─── Session ─────────────────────────────────────────────────────────────────

export interface Session {
  id: string;
  spot_id: string;
  user_id: string;
  started_at: string;
  ended_at: string | null;
  available_until: string | null;
}

export interface ActiveSession extends Session {
  ended_at: null;
  spot: {
    spot_number: string;
    level_label: string;
    owner: { apartment: string };
  };
  reclaim_alert: ReclaimAlert | null;
}

// ─── Reclaim ──────────────────────────────────────────────────────────────────

export type ReclaimStatus = 'pending' | 'acknowledged' | 'resolved' | 'cancelled';

export type ReclaimAvailabilitySource = 'schedule' | 'window';

export interface ReclaimAlert {
  id: string;
  session_id: string;
  spot_id: string;
  status: ReclaimStatus;
  availability_source: ReclaimAvailabilitySource;
  created_at: string;
  acknowledged_at: string | null;
  resolved_at: string | null;
}

// ─── User ─────────────────────────────────────────────────────────────────────

export interface User {
  id: string;
  name: string;
  email: string;
  apartment: string;
  phone: string | null;
  is_admin: boolean;
  building_id: string;
  building: {
    id: string;
    name: string;
    timezone: string;
  };
}

export interface RegistrationPreview {
  found: boolean;
  code?: string;
  user?: {
    name: string;
    apartment: string;
    email: string;
    phone: string;
    building: {
      id: string;
      name: string;
      timezone: string;
    };
  };
  building?: {
    id: string;
    name: string;
    timezone: string;
  };
  message?: string;
}

// ─── Availability ─────────────────────────────────────────────────────────────

export interface AvailabilitySchedule {
  id: string;
  spot_id: string;
  days_of_week: number[];
  start_time: string;
  end_time: string;
}

export type WindowType = 'free' | 'block';

export interface AvailabilityWindow {
  id: string;
  spot_id: string;
  type: WindowType;
  starts_at: string;
  ends_at: string;
}

// ─── Notification preferences (migration 066) ───────────────────────────────

export type NotificationTopic =
  | 'reclaim_alerts'
  | 'my_sessions'
  | 'my_spot_activity'
  | 'building_broadcasts'
  | 'system_messages';

export const NOTIFICATION_TOPICS: NotificationTopic[] = [
  'reclaim_alerts',
  'my_sessions',
  'my_spot_activity',
  'building_broadcasts',
  'system_messages',
];

/** Per-topic default when the user has no row in user_notification_preferences. */
export const NOTIFICATION_TOPIC_DEFAULTS: Record<NotificationTopic, boolean> = {
  reclaim_alerts:      true,
  my_sessions:         true,
  my_spot_activity:    true,
  building_broadcasts: true,
  system_messages:     true,
};

/**
 * Topics the user cannot turn off individually. The master toggle is the
 * only mute path for these — they're critical to the parking flow
 * (occupant must know when their session is ending or being reclaimed),
 * or operational and rare enough that suppressing them silently would
 * be misleading (admin/system announcements).
 */
export const LOCKED_ON_TOPICS: NotificationTopic[] = ['reclaim_alerts', 'system_messages'];

export interface NotificationPreferences {
  master: boolean;
  topics: Record<NotificationTopic, boolean>;
}

// ─── API response wrapper ─────────────────────────────────────────────────────

export interface ApiSuccess<T> {
  data: T;
}

export interface ApiError {
  error: {
    code: string;
    message: string;
  };
}

export type ApiResponse<T> = ApiSuccess<T> | ApiError;
